import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Filter, Eye, Check, X, Mail, Phone, Calendar, Package, MessageSquare, DollarSign, Trash2, Download } from 'lucide-react';
import './AdminBookings.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'confirm' or 'reject'
  const [actionNotes, setActionNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const fetchBookings = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/admin/bookings${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (booking, type) => {
    setSelectedBooking(booking);
    setActionType(type);
    setActionNotes('');
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedBooking(null);
    setActionType(null);
    setActionNotes('');
  };

  const openDetailModal = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBooking(null);
  };

  const handleStatusUpdate = async () => {
    if (!selectedBooking || !actionType) return;
    
    setProcessing(true);
    const newStatus = actionType === 'confirm' ? 'confirmed' : 'rejected';
    
    try {
      await axios.put(
        `${API}/admin/bookings/${selectedBooking.id}`,
        { 
          status: newStatus,
          notes: actionNotes || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchBookings();
      closeActionModal();
      alert(`Booking ${newStatus}! ${selectedBooking.customer_email ? 'Email notification sent to customer.' : ''}`);
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    } finally {
      setProcessing(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking status');
    }
  };

  const handleDeleteBooking = async (booking) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the booking from "${booking.customer_name}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/admin/bookings/${booking.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBookings();
      if (showDetailModal) closeDetailModal();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('Failed to delete booking');
    }
  };

  const handleExportCSV = async (type) => {
    try {
      const endpoint = type === 'customers' 
        ? `${API}/admin/customers/export/csv`
        : `${API}/admin/bookings/export/csv${filter !== 'all' ? `?status_filter=${filter}` : ''}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', type === 'customers' 
        ? `tropiluxe_customers_${new Date().toISOString().slice(0,10)}.csv`
        : `tropiluxe_bookings_${new Date().toISOString().slice(0,10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  const filteredBookings = bookings.filter(booking =>
    booking.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    booking.customer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      confirmed: '#10b981',
      rejected: '#ef4444',
      completed: '#6366f1',
      cancelled: '#64748b'
    };
    return colors[status] || '#64748b';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: '#10b981',
      unpaid: '#f59e0b',
      initiated: '#3b82f6',
      expired: '#ef4444',
      refunded: '#8b5cf6'
    };
    return colors[status] || '#64748b';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <div className="admin-bookings-loading">Loading bookings...</div>;
  }

  return (
    <div className="admin-bookings">
      <div className="bookings-header">
        <div>
          <h2>Bookings Management</h2>
          <p>Manage customer bookings and inquiries</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="export-buttons">
            <button 
              onClick={() => handleExportCSV('bookings')} 
              className="btn-export"
              data-testid="export-bookings-csv"
              title="Export Bookings"
            >
              <Download size={16} /> Bookings CSV
            </button>
            <button 
              onClick={() => handleExportCSV('customers')} 
              className="btn-export"
              data-testid="export-customers-csv"
              title="Export Customers"
            >
              <Download size={16} /> Customers CSV
            </button>
          </div>
          <div className="bookings-stats">
            <div className="stat-item">
              <span className="stat-value">{bookings.filter(b => b.status === 'pending').length}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{bookings.filter(b => b.status === 'confirmed').length}</span>
              <span className="stat-label">Confirmed</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bookings-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="booking-search-input"
          />
        </div>
        
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
          data-testid="booking-status-filter"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="bookings-table-container">
        {filteredBookings.length > 0 ? (
          <table className="bookings-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Event</th>
                <th>Date</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.id}>
                  <td>
                    <div className="customer-info">
                      <span className="customer-name">{booking.customer_name}</span>
                      <span className="customer-email">{booking.customer_email}</span>
                      <span className="customer-phone">{booking.customer_phone}</span>
                    </div>
                  </td>
                  <td>
                    <div className="event-info">
                      <span className="event-type">{booking.event_type}</span>
                      {booking.package_name && (
                        <span className="package-name">{booking.package_name}</span>
                      )}
                    </div>
                  </td>
                  <td className="date-cell">{booking.event_date || 'Not specified'}</td>
                  <td>
                    <div className="payment-info">
                      <span 
                        className="payment-badge"
                        style={{ 
                          background: `${getPaymentStatusColor(booking.payment_status)}15`,
                          color: getPaymentStatusColor(booking.payment_status)
                        }}
                      >
                        {booking.payment_status || 'unpaid'}
                      </span>
                      {booking.amount && (
                        <span className="amount">${booking.amount.toFixed(2)}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ 
                        background: `${getStatusColor(booking.status)}20`,
                        color: getStatusColor(booking.status)
                      }}
                    >
                      {booking.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => openDetailModal(booking)}
                        className="btn-view"
                        title="View Details"
                        data-testid={`view-booking-${booking.id}`}
                      >
                        <Eye size={16} />
                      </button>
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => openActionModal(booking, 'confirm')}
                            className="btn-confirm"
                            title="Accept Booking"
                            data-testid={`accept-booking-${booking.id}`}
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => openActionModal(booking, 'reject')}
                            className="btn-reject"
                            title="Reject Booking"
                            data-testid={`reject-booking-${booking.id}`}
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'completed')}
                          className="btn-complete"
                          title="Mark Complete"
                          data-testid={`complete-booking-${booking.id}`}
                        >
                          Complete
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteBooking(booking)}
                        className="btn-delete"
                        title="Delete Booking"
                        data-testid={`delete-booking-${booking.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            No bookings found
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedBooking && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeDetailModal}>
              <X size={24} />
            </button>
            
            <div className="modal-header">
              <h3>Booking Details</h3>
              <span 
                className="status-badge large"
                style={{ 
                  background: `${getStatusColor(selectedBooking.status)}20`,
                  color: getStatusColor(selectedBooking.status)
                }}
              >
                {selectedBooking.status}
              </span>
            </div>
            
            <div className="detail-grid">
              <div className="detail-section">
                <h4>Customer Information</h4>
                <div className="detail-row">
                  <Mail size={16} />
                  <span>{selectedBooking.customer_email}</span>
                </div>
                <div className="detail-row">
                  <Phone size={16} />
                  <span>{selectedBooking.customer_phone || 'Not provided'}</span>
                </div>
              </div>
              
              <div className="detail-section">
                <h4>Event Details</h4>
                <div className="detail-row">
                  <Calendar size={16} />
                  <span>{selectedBooking.event_date || 'Date not specified'}</span>
                </div>
                <div className="detail-row">
                  <Package size={16} />
                  <span>{selectedBooking.package_name || selectedBooking.event_type}</span>
                </div>
                {selectedBooking.guest_count && (
                  <div className="detail-row">
                    <span className="label">Guests:</span>
                    <span>{selectedBooking.guest_count}</span>
                  </div>
                )}
              </div>
              
              <div className="detail-section">
                <h4>Payment</h4>
                <div className="detail-row">
                  <DollarSign size={16} />
                  <span>{selectedBooking.amount ? `$${selectedBooking.amount.toFixed(2)}` : 'Not set'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span 
                    className="payment-badge"
                    style={{ 
                      background: `${getPaymentStatusColor(selectedBooking.payment_status)}15`,
                      color: getPaymentStatusColor(selectedBooking.payment_status)
                    }}
                  >
                    {selectedBooking.payment_status || 'unpaid'}
                  </span>
                </div>
              </div>
              
              {selectedBooking.message && (
                <div className="detail-section full-width">
                  <h4>Customer Message</h4>
                  <div className="message-box">
                    <MessageSquare size={16} />
                    <p>{selectedBooking.message}</p>
                  </div>
                </div>
              )}
              
              {selectedBooking.notes && (
                <div className="detail-section full-width">
                  <h4>Admin Notes</h4>
                  <div className="notes-box">
                    <p>{selectedBooking.notes}</p>
                  </div>
                </div>
              )}
              
              <div className="detail-section full-width">
                <h4>Booking Info</h4>
                <div className="detail-row">
                  <span className="label">Reference:</span>
                  <span className="booking-ref">{selectedBooking.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Created:</span>
                  <span>{formatDate(selectedBooking.created_at)}</span>
                </div>
              </div>
            </div>
            
            {selectedBooking.status === 'pending' && (
              <div className="modal-actions">
                <button 
                  onClick={() => { closeDetailModal(); openActionModal(selectedBooking, 'confirm'); }}
                  className="btn-primary confirm"
                >
                  <Check size={18} /> Accept Booking
                </button>
                <button 
                  onClick={() => { closeDetailModal(); openActionModal(selectedBooking, 'reject'); }}
                  className="btn-primary reject"
                >
                  <X size={18} /> Reject Booking
                </button>
              </div>
            )}

            <div className="modal-actions" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
              <button 
                onClick={() => handleDeleteBooking(selectedBooking)}
                className="btn-primary reject"
                data-testid="modal-delete-booking-btn"
              >
                <Trash2 size={18} /> Delete Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Confirm/Reject) */}
      {showActionModal && selectedBooking && (
        <div className="modal-overlay" onClick={closeActionModal}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeActionModal}>
              <X size={24} />
            </button>
            
            <div className={`modal-header ${actionType}`}>
              <div className={`action-icon ${actionType}`}>
                {actionType === 'confirm' ? <Check size={32} /> : <X size={32} />}
              </div>
              <h3>{actionType === 'confirm' ? 'Accept Booking' : 'Reject Booking'}</h3>
              <p className="modal-subtitle">
                {actionType === 'confirm' 
                  ? 'The customer will receive a confirmation email'
                  : 'The customer will be notified of the rejection'}
              </p>
            </div>
            
            <div className="booking-summary">
              <div className="summary-row">
                <span className="label">Customer:</span>
                <span>{selectedBooking.customer_name}</span>
              </div>
              <div className="summary-row">
                <span className="label">Package:</span>
                <span>{selectedBooking.package_name || selectedBooking.event_type}</span>
              </div>
              <div className="summary-row">
                <span className="label">Email:</span>
                <span>{selectedBooking.customer_email}</span>
              </div>
            </div>
            
            <div className="notes-input-section">
              <label>
                {actionType === 'confirm' ? 'Add a note for the customer (optional):' : 'Reason for rejection (optional):'}
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder={actionType === 'confirm' 
                  ? 'e.g., Looking forward to hosting your special evening!'
                  : 'e.g., Unfortunately, we are fully booked on this date...'}
                rows={4}
                data-testid="modal-notes-input"
              />
            </div>
            
            <div className="modal-actions">
              <button onClick={closeActionModal} className="btn-secondary" data-testid="modal-cancel-btn">
                Cancel
              </button>
              <button 
                onClick={handleStatusUpdate} 
                className={`btn-primary ${actionType}`}
                disabled={processing}
                data-testid="modal-submit-btn"
              >
                {processing ? 'Sending...' : (actionType === 'confirm' ? 'Accept & Send Email' : 'Reject & Send Email')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
